import React, { useEffect } from "react";
import { Plus, History, Search as SearchIcon, Trash, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/context/LanguageContext";

interface ChatSummary {
  id: string;
  title: string;
  timestamp: string;
}

export const ChatHeaderControls: React.FC = () => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [chatToDelete, setChatToDelete] = React.useState<ChatSummary | null>(null);
  const [recentChats, setRecentChats] = React.useState<ChatSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { chatId } = useParams<{ chatId: string }>();
  const { t } = useLanguage();

  useEffect(() => {
    fetchChats();

    const channel = supabase.channel("chat-updates").on("postgres_changes", {
      event: "*",
      schema: "public",
      table: "content",
      filter: `type=eq.chat`
    }, () => {
      fetchChats();
    }).subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchChats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase.from("content").select("id, title, updated_at").eq("type", "chat").eq("user_id", user.id).order("updated_at", { ascending: false }).limit(10);
      if (error) throw error;

      const formattedChats: ChatSummary[] = (data || []).map(chat => ({
        id: chat.id,
        title: chat.title,
        timestamp: formatDistanceToNow(new Date(chat.updated_at), { addSuffix: true })
      }));
      setRecentChats(formattedChats);
    } catch (error) {
      console.error("Error fetching chats:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredChats = recentChats.filter(chat => chat.title.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleDeleteClick = (chat: ChatSummary) => {
    setChatToDelete(chat);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (chatToDelete) {
      try {
        const { error } = await supabase.from("content").delete().eq("id", chatToDelete.id);
        if (error) throw error;
        toast({
          title: "Conversation deleted",
          description: "The conversation has been permanently deleted."
        });
        fetchChats();
      } catch (error) {
        console.error("Error deleting chat:", error);
        toast({
          title: "Error",
          description: "Failed to delete conversation.",
          variant: "destructive"
        });
      } finally {
        setDeleteDialogOpen(false);
        setChatToDelete(null);
      }
    }
  };

  const handleNewChat = () => {
    navigate("/chat");
  };

  const handleOpenChat = (chat: ChatSummary) => {
    navigate(`/chat/${chat.id}`);
    toast({
      title: "Opening conversation",
      description: chat.title
    });
  };

  return (
    <>
      <TooltipProvider>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={handleNewChat} className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:text-primary" aria-label="New conversation">
                <Plus className="h-4 w-4 flex-shrink-0" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t("common.newChat")}</p>
            </TooltipContent>
          </Tooltip>

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:text-primary" aria-label="History">
                    <History className="h-4 w-4 flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("common.chatHistory")}</p>
              </TooltipContent>
            </Tooltip>

            <DropdownMenuContent align="end" className="z-50 w-80 border border-border bg-card shadow-lg">
              <div className="p-2">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Search conversations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-9 pl-9" />
                </div>
              </div>

              <DropdownMenuSeparator />

              <div className="max-h-[300px] overflow-y-auto">
                {loading ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">Loading conversations...</div>
                ) : filteredChats.length > 0 ? (
                  filteredChats.map(chat => {
                    const isActive = chatId === chat.id;
                    return (
                      <DropdownMenuItem key={chat.id} className={`group/chat-item flex cursor-pointer items-center justify-between gap-2 px-3 py-2 ${isActive ? "bg-primary/10 hover:bg-primary/15" : ""}`} onSelect={e => { e.preventDefault(); handleOpenChat(chat); }}>
                        <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                          <span className={`w-full truncate text-sm font-medium ${isActive ? "text-primary" : "text-foreground"}`}>{chat.title}</span>
                          <span className="text-xs text-muted-foreground">{chat.timestamp}</span>
                        </div>
                        <button onClick={e => { e.stopPropagation(); handleDeleteClick(chat); }} className="flex-shrink-0 rounded-md p-1.5 text-destructive opacity-0 transition-opacity group-hover/chat-item:opacity-100 hover:bg-destructive/10" aria-label="Delete conversation">
                          <Trash className="h-4 w-4" />
                        </button>
                      </DropdownMenuItem>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center px-4 py-8">
                    <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                      <MessageSquare className="h-8 w-8 text-primary" />
                    </div>
                    <p className="mb-1 text-sm font-medium text-foreground">{searchQuery ? "No matches found" : "No conversations yet"}</p>
                    <p className="mb-4 text-center text-xs text-muted-foreground">{searchQuery ? "Try adjusting your search" : "Start a conversation to see your history"}</p>
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TooltipProvider>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{chatToDelete?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
